import { useAbility } from "config/abilityContext";
import { formatMoney } from "helpers/numberHelper";
import React from "react";
import { useTranslation } from "react-i18next";
import { formatToLocaleDate } from "utils/dateUtils";
import { fallback } from "utils/stringUtils";

interface Props { student: any; }

const InitialsAvatar: React.FC<{ name: string; size?: "md" | "xl" }> = ({ name, size = "xl" }) => {
  const initials =
    name
      ?.split(" ")
      .filter(Boolean)
      .map((p) => p[0]?.toUpperCase())
      .slice(0, 2)
      .join("") || "—";

  const sizeClass = size === "xl" ? "avatar-xxl fs-3" : "avatar-md fs-6";

  return (
    <div className={`d-flex align-items-center justify-content-center avatar ${sizeClass} bg-light text-dark border`}>
      <span>{initials}</span>
    </div>
  );
};

const Row: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) =>
  value ? (
    <div className="row mb-2">
      <div className="col-md-3 col-6 fw-medium text-dark">{label}</div>
      <div className="col-md-9 col-6">{value}</div>
    </div>
  ) : null;



const StudentSidebar: React.FC<Props> = ({ student }) => {
  const { t } = useTranslation();
  const ability = useAbility();
  if (!student) return null;

  const user = student?.user;
  const guardians = Array.isArray(student?.linkedGuardians) ? student.linkedGuardians : [];

  const fullName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
    t("unknown", "Unknown");

  return (
    <>
      {ability.can("getAll", "Student") && (
        <>
          {/* Header card */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                {user?.photo ? (
                  <img src={user.photo} alt="avatar" className="avatar avatar-xxl border" />
                ) : (
                  <InitialsAvatar name={fullName} />
                )}
                <div className="flex-grow-1">
                  <h4 className="mb-1 text-truncate">{fullName}</h4>
                  {student.status && (
                    <div className="text-primary mb-1">
                      <i className="ti ti-badge" /> {t("status")}:{" "}
                      <span className="text-lowercase">{student.status}</span>
                    </div>
                  )}
                  {student.createdAt && (
                    <div className="text-muted mb-1">
                      <i className="ti ti-calendar" /> {t("joined_on", "Joined")} :{" "}
                      {formatToLocaleDate(student.createdAt)}
                    </div>
                  )}
                  {student.massarCode && (
                    <div className="text-muted">
                      <i className="ti ti-id" /> {t("massar_code", "Massar Code")} :{" "}
                      {student.massarCode}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Basic info */}
          {(user?.gender || user?.birthDate || user?.cityOfBirth || user?.city || user?.address || user?.zipCode) && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="ti ti-id" /> {t("basic_information")}
                </h5>
              </div>
              <div className="card-body">
                <Row label={t("gender")} value={user?.gender && t(user.gender)} />
                <Row label={t("birth_date")} value={user?.birthDate && formatToLocaleDate(user.birthDate)} />
                <Row label={t("city_of_birth")} value={user?.cityOfBirth && fallback(user.cityOfBirth)} />
                <Row label={t("city")} value={user?.city && fallback(user.city)} />
                <Row label={t("address")} value={user?.address && fallback(user.address)} />
                <Row label={t("zip_code")} value={user?.zipCode && fallback(user.zipCode)} />
              </div>
            </div>
          )}

          {/* Primary contact */}
          {(user?.phoneNumber || user?.email) && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="ti ti-address-book" /> {t("primary_contact_info")}
                </h5>
              </div>
              <div className="card-body">
                <Row
                  label={t("phone_number")}
                  value={
                    user?.phoneNumber ? (
                      <a href={`tel:${user.phoneNumber}`} className="text-decoration-none">
                        <i className="ti ti-phone me-1" />
                        {user.phoneNumber}
                      </a>
                    ) : undefined
                  }
                />
                <Row
                  label={t("email_address")}
                  value={
                    user?.email ? (
                      <a href={`mailto:${user.email}`} className="text-decoration-none">
                        <i className="ti ti-mail me-1" />
                        {user.email}
                      </a>
                    ) : undefined
                  }
                />
              </div>
            </div>
          )}

          {ability.can("getAll", "Guardian") && guardians.length > 0 && (
            <div className="card mb-4">
              <div className="card-header d-flex align-items-center justify-content-between">
                <h5 className="mb-0">
                  <i className="ti ti-users" /> {t("guardians")}
                </h5>
              </div>
              <div className="card-body">
                <ul className="list-unstyled mb-0">
                  {guardians.map((g: any, index: number) => {
                    const gu = g?.guardian?.user || {};
                    const gName = [gu.firstName, gu.lastName].filter(Boolean).join(" ").trim() || "—";
                    const relKey = g?.relationship || "guardian";
                    const relText = t(relKey);
                    const icon = "ti ti-user";

                    return (
                      <li key={g.id || index}>
                        <div className="d-flex align-items-start gap-3">
                          {/* Guardian avatar */}
                          {gu.photo ? (
                            <img src={gu.photo} alt="guardian" className="avatar avatar-md border" />
                          ) : (
                            <InitialsAvatar name={gName} size="md" />
                          )}

                          {/* Main block */}
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center flex-wrap gap-2">
                              <span className="fw-semibold text-dark">{gName}</span>
                              <span className="badge rounded-pill bg-light text-dark">
                                <i className={`${icon} me-1`} />
                                {relText}
                              </span>
                            </div>

                            <div className="mt-1 d-flex flex-column gap-1">
                              {gu.phoneNumber && (
                                <a href={`tel:${gu.phoneNumber}`} className="text-decoration-none">
                                  <i className="ti ti-phone me-1" />
                                  {gu.phoneNumber}
                                </a>
                              )}
                              {gu.email && (
                                <a href={`mailto:${gu.email}`} className="text-decoration-none">
                                  <i className="ti ti-mail me-1" />
                                  {gu.email}
                                </a>
                              )}
                              {gu.address && (
                                <div className="text-muted">
                                  <i className="ti ti-map-pin me-1" />
                                  {gu.address}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Separator */}
                        {index < guardians.length - 1 && <hr className="my-3" />}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default StudentSidebar;
